import type { TFunction } from 'i18next';

import { CREDIT_PACKS, type CreditPack } from '../../config/creditPacks';

/** Default pack when opening /checkout without a plan (logged-in upgrade flows). */
export const DEFAULT_CREDIT_CHECKOUT_PLAN = 'pack-monthly';

/** Set when user starts Cashfree checkout — used to return to /checkout after cancel. */
export const CHECKOUT_ACTIVE_PLAN_KEY = 'checkout_active_plan_id';

export function readCheckoutActivePlanId(): string {
  if (typeof window === 'undefined') return DEFAULT_CREDIT_CHECKOUT_PLAN;
  return sessionStorage.getItem(CHECKOUT_ACTIVE_PLAN_KEY)
    ?? readPlanIdFromUrl()
    ?? DEFAULT_CREDIT_CHECKOUT_PLAN;
}

export function stashCheckoutActivePlan(planId: string): void {
  try { sessionStorage.setItem(CHECKOUT_ACTIVE_PLAN_KEY, planId); } catch (_) {}
}

export function clearCheckoutActivePlan(): void {
  try { sessionStorage.removeItem(CHECKOUT_ACTIVE_PLAN_KEY); } catch (_) {}
}

export const PRICING_PAGE_PACK_MAP: Record<string, string> = {
  monthly: 'pack-monthly',
  quarterly: 'pack-quarterly',
  halfyearly: 'pack-halfyearly',
};

export function resolvePackFromId(id: string | undefined): CreditPack | null {
  if (!id) return null;
  const packId = PRICING_PAGE_PACK_MAP[id] ?? id;
  return CREDIT_PACKS.find((p) => p.id === packId) ?? null;
}

export function readPlanIdFromUrl(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  return new URLSearchParams(window.location.search).get('plan') ?? undefined;
}

/** Normalize user-entered Indian mobile to 10 digits (handles +91 prefix). */
export function normalizeIndianPhoneInput(phone: string): string {
  const digits = String(phone || '').replace(/\D/g, '');
  if (digits.length >= 10) return digits.slice(-10);
  return digits;
}

export function isValidIndianPhone(phone: string): boolean {
  const digits = normalizeIndianPhoneInput(phone);
  return digits.length === 10 && /^[6-9]/.test(digits) && digits !== '9999999999';
}

type CashfreeCheckoutResult = {
  error?: { message?: string; code?: string; type?: string };
  paymentDetails?: { paymentStatus?: string; paymentMessage?: string };
  redirect?: boolean;
};

const CASHFREE_FAILURE_STATUSES = new Set([
  'FAILED',
  'CANCELLED',
  'CANCELLED_BY_USER',
  'USER_DROPPED',
  'FLAGGED',
  'EXPIRED',
  'TERMINATED',
]);

const CASHFREE_SUCCESS_STATUSES = new Set(['SUCCESS', 'PAID', 'ACTIVE']);

/** Map create-cashfree-subscription API errors to checkout copy. */
export function mapSubscriptionCreateError(payload: {
  error?: string;
  code?: string;
  details?: unknown;
}): string | null {
  if (payload.code === 'cashfree_config') {
    return payload.error || 'Payment gateway is not configured. Please try again later.';
  }
  if (payload.code === 'cashfree_rejected') {
    return payload.error || 'Payment provider rejected the subscription request.';
  }
  if (payload.code === 'cashfree_subscriptions_unavailable') {
    return payload.error || 'Cashfree subscriptions are not enabled on the production merchant account. Use sandbox mode for testing.';
  }
  if (payload.code === 'invalid_customer') {
    return typeof payload.details === 'string'
      ? payload.details
      : 'Enter a valid 10-digit Indian mobile number below.';
  }
  if (typeof payload.details === 'string' && payload.details.trim()) {
    return payload.details.trim();
  }
  return payload.error?.trim() || null;
}

export type CashfreeRuntimeEnv = 'sandbox' | 'production';

export function isLocalDevPaymentHost(hostname?: string): boolean {
  if (typeof window !== 'undefined' && !hostname) {
    return isLocalDevPaymentHost(window.location.hostname);
  }
  const h = String(hostname || '').toLowerCase();
  return h === 'localhost' || h === '127.0.0.1' || h.endsWith('.local');
}

/**
 * Cashfree SDK mode for this browser tab.
 *
 * Priority:
 * 1. VITE_B2C_CASHFREE_ENV=PROD|TEST — B2C build override (Netlify / .env)
 * 2. Host — localhost/*.local → sandbox; deployed hosts → production
 *
 * B2B uses VITE_B2B_CASHFREE_ENV in its own repo. Shared Supabase backend picks
 * TEST/PROD credentials from the request Origin/returnUrl per checkout.
 */
export function getCashfreeSdkMode(): CashfreeRuntimeEnv {
  const forced = String(
    import.meta.env.VITE_B2C_CASHFREE_ENV
      || import.meta.env.VITE_CASHFREE_ENV
      || '',
  ).trim().toUpperCase();
  if (forced === 'PROD' || forced === 'PRODUCTION') return 'production';
  if (forced === 'TEST' || forced === 'SANDBOX') return 'sandbox';

  if (typeof window === 'undefined') return 'sandbox';
  return isLocalDevPaymentHost(window.location.hostname) ? 'sandbox' : 'production';
}

/** Infer sandbox vs production from edge-function response metadata. */
export function detectCashfreeOrderSessionEnv(payload: {
  cashfree_env?: string;
  payment_link?: string;
}): CashfreeRuntimeEnv | null {
  if (payload.cashfree_env === 'PROD') return 'production';
  if (payload.cashfree_env === 'TEST') return 'sandbox';
  const link = String(payload.payment_link || '').toLowerCase();
  if (link.includes('sandbox.cashfree.com')) return 'sandbox';
  if (link.includes('www.cashfree.com') || link.includes('api.cashfree.com')) return 'production';
  return null;
}

/** Block checkout when Supabase secrets and VITE_CASHFREE_ENV disagree. */
export function cashfreeEnvMismatchMessage(
  sessionEnv: CashfreeRuntimeEnv,
  sdkMode: CashfreeRuntimeEnv = getCashfreeSdkMode(),
): string {
  const backendLabel = sessionEnv === 'sandbox' ? 'TEST (sandbox)' : 'PROD (live)';
  const frontendLabel = sdkMode === 'sandbox' ? 'TEST (sandbox)' : 'PROD (live)';
  return `Payment environment mismatch: the server created a ${backendLabel} session but this page is in ${frontendLabel} mode. Refresh and try again from the same host (localhost uses sandbox; production URLs use live Cashfree).`;
}

export function validateCashfreeOrderSessionEnv(payload: {
  cashfree_env?: string;
  payment_link?: string;
}): string | null {
  const sessionEnv = detectCashfreeOrderSessionEnv(payload);
  if (!sessionEnv) return null;
  const sdkMode = getCashfreeSdkMode();
  return sessionEnv === sdkMode ? null : cashfreeEnvMismatchMessage(sessionEnv, sdkMode);
}

/** Block subscription checkout when Supabase secrets and VITE_CASHFREE_ENV disagree. */
export function validateCashfreeSubscriptionSessionEnv(payload: {
  cashfree_env?: string;
}): string | null {
  if (payload.cashfree_env === 'PROD') {
    return getCashfreeSdkMode() === 'production'
      ? null
      : cashfreeEnvMismatchMessage('production', getCashfreeSdkMode());
  }
  if (payload.cashfree_env === 'TEST') {
    return getCashfreeSdkMode() === 'sandbox'
      ? null
      : cashfreeEnvMismatchMessage('sandbox', getCashfreeSdkMode());
  }
  return null;
}

export function getCashfreeOrderCheckoutUrl(
  mode: CashfreeRuntimeEnv = getCashfreeSdkMode(),
): string {
  return mode === 'production'
    ? 'https://api.cashfree.com/pg/view/sessions/checkout'
    : 'https://sandbox.cashfree.com/pg/view/sessions/checkout';
}

export function getCashfreeSubscriptionCheckoutUrl(
  mode: CashfreeRuntimeEnv = getCashfreeSdkMode(),
): string {
  return mode === 'production'
    ? 'https://api.cashfree.com/pg/view/sessions/checkout/subs'
    : 'https://sandbox.cashfree.com/pg/view/sessions/checkout/subs';
}

/**
 * POST to Cashfree hosted one-time order checkout (same endpoint the JS SDK uses for _self).
 * Do not navigate to /pg/view/{order_id} — that URL is not a valid checkout page.
 */
export function postCashfreeOrderCheckout(
  paymentSessionId: string,
  mode: CashfreeRuntimeEnv = getCashfreeSdkMode(),
): void {
  if (typeof document === 'undefined') return;

  const browserMeta = btoa(JSON.stringify({
    userAgent: window.navigator.userAgent,
  }));

  const form = document.createElement('form');
  form.method = 'POST';
  form.action = getCashfreeOrderCheckoutUrl(mode);
  form.target = '_self';
  form.style.display = 'none';

  const fields: Record<string, string> = {
    payment_session_id: paymentSessionId,
    x_request_id: `x_request_id_form_order_atom_${Date.now()}`,
    form_id: 'form_order_id_atom',
    browser_meta: browserMeta,
    platform: 'web',
  };

  for (const [name, value] of Object.entries(fields)) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.appendChild(input);
  }

  document.body.appendChild(form);
  form.submit();
}

/**
 * POST to Cashfree hosted subscription checkout (same fields as Cashfree JS SDK).
 * Prefer this over subscriptionsCheckout() — avoids SDK mode/context races.
 */
export function postCashfreeSubscriptionCheckout(
  subsSessionId: string,
  mode: CashfreeRuntimeEnv = getCashfreeSdkMode(),
): void {
  if (typeof document === 'undefined') return;

  const browserMeta = btoa(JSON.stringify({
    userAgent: window.navigator.userAgent,
  }));

  const form = document.createElement('form');
  form.method = 'POST';
  form.action = getCashfreeSubscriptionCheckoutUrl(mode);
  form.target = '_self';
  form.style.display = 'none';

  const fields: Record<string, string> = {
    subs_session_id: subsSessionId,
    x_request_id: `x_request_id_form_subs_atom_${Date.now()}`,
    form_id: 'form_subs_id_atom',
    browser_meta: browserMeta,
    platform: 'web',
  };

  for (const [name, value] of Object.entries(fields)) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.appendChild(input);
  }

  document.body.appendChild(form);
  form.submit();
}

export function isLocalDevCheckoutHost(): boolean {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  return host === 'localhost' || host === '127.0.0.1' || host.endsWith('.local');
}

/** localhost cannot use Cashfree _modal reliably (postMessage origin mismatch, PROD whitelist). */
export function shouldUseCashfreeFullPageCheckout(
  sdkMode: CashfreeRuntimeEnv = getCashfreeSdkMode(),
): boolean {
  return isLocalDevCheckoutHost() || sdkMode === 'production';
}

/** PROD Cashfree JS SDK requires domain whitelisting — localhost is not allowed. */
export function shouldUseCashfreeHostedPaymentPage(
  sdkMode: CashfreeRuntimeEnv = getCashfreeSdkMode(),
): boolean {
  return shouldUseCashfreeFullPageCheckout(sdkMode);
}

/** Map Cashfree SDK / gateway codes to user-friendly copy. */
export function mapCashfreePaymentError(raw?: string, code?: string): string {
  const normalized = (code || raw || '').toUpperCase().replace(/\s+/g, '_');
  const friendly: Record<string, string> = {
    PAYMENT_SESSION_ID_INVALID: 'Payment session could not be opened. Refresh the page and try checkout again from the same site (localhost = sandbox, production = live).',
    ORDER_TOKEN_INVALID: 'Payment session could not be opened. Refresh and try again — localhost uses sandbox Cashfree, production uses live.',
    DOMAIN_NOT_WHITELISTED: 'This site is not whitelisted in your Cashfree merchant dashboard. For local PROD testing we redirect to Cashfree\'s hosted page; for embedded checkout, whitelist your domain under merchant.cashfree.com → Developers.',
    INSUFFICIENT_FUNDS: 'Your card has insufficient funds. Try another card or UPI.',
    TIMED_OUT: 'The payment timed out. Please try again.',
    USER_DROPPED: 'Payment was cancelled before completion. No money was deducted.',
    CANCELLED: 'Payment was cancelled. No money was deducted.',
    CANCELLED_BY_USER: 'Payment was cancelled. No money was deducted.',
    PAYMENT_CANCELLED: 'Payment was cancelled. No money was deducted.',
    AUTHENTICATION_FAILED: 'Card authentication failed. Check your details or try another method.',
    FLAGGED: 'This payment could not be completed. Try a different payment method.',
    FAILED: 'Your payment could not be processed. No money was deducted.',
  };

  if (friendly[normalized]) return friendly[normalized];

  const lower = (raw || '').toLowerCase();
  if (/cancel|abort|dropped|closed/i.test(lower)) {
    return friendly.CANCELLED;
  }
  if (/insufficient/i.test(lower)) return friendly.INSUFFICIENT_FUNDS;
  if (/timeout|timed out/i.test(lower)) return friendly.TIMED_OUT;
  if (/auth/i.test(lower)) return friendly.AUTHENTICATION_FAILED;
  if (/not enabled or approved|whitelist|broken link/i.test(lower)) {
    return friendly.DOMAIN_NOT_WHITELISTED;
  }
  if (/token is not present|order_token_invalid/i.test(lower)) {
    return friendly.ORDER_TOKEN_INVALID;
  }

  return raw?.trim()
    || 'Your payment could not be processed. No money was deducted. Please try again or use a different payment method.';
}

export type CashfreeCheckoutOutcome = 'success' | 'failed' | 'cancelled';

/** Interpret Cashfree JS SDK checkout() result after the modal closes. */
export function interpretCashfreeCheckoutResult(result: unknown): CashfreeCheckoutOutcome {
  const r = (result ?? {}) as CashfreeCheckoutResult;

  if (r.error) {
    const msg = (r.error.message || r.error.code || '').toLowerCase();
    if (/cancel|dropped|closed|abort/i.test(msg)) return 'cancelled';
    return 'failed';
  }

  const status = (r.paymentDetails?.paymentStatus || '').toUpperCase();
  if (status) {
    if (CASHFREE_SUCCESS_STATUSES.has(status)) return 'success';
    if (CASHFREE_FAILURE_STATUSES.has(status)) {
      return status === 'USER_DROPPED' || status.includes('CANCEL') ? 'cancelled' : 'failed';
    }
  }

  if (r.redirect) return 'success';

  // Modal closed without a definitive success payload
  return 'cancelled';
}

export function cashfreeFailureMessage(result: unknown): string {
  const r = (result ?? {}) as CashfreeCheckoutResult;
  const status = r.paymentDetails?.paymentStatus;
  const detail = r.paymentDetails?.paymentMessage || r.error?.message;
  return mapCashfreePaymentError(detail, r.error?.code || status);
}

export type CashfreeOrderVerifyStatus = 'completed' | 'failed' | 'abandoned' | 'pending';

/** Poll verify-cashfree-payment once (manual auth path). */
export async function verifyCashfreeOrderStatus(
  orderId: string,
  accessToken: string,
): Promise<CashfreeOrderVerifyStatus> {
  const functionUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/verify-cashfree-payment`;
  const res = await fetch(functionUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
    },
    body: JSON.stringify({ order_id: orderId }),
  });
  const data = await res.json().catch(() => ({}));
  if (data?.success || data?.status === 'completed') return 'completed';
  if (data?.status === 'abandoned') return 'abandoned';
  if (data?.status === 'failed') return 'failed';
  return 'pending';
}

export function localizedPackName(packId: string, t: TFunction): string {
  switch (packId) {
    case 'pack-monthly':
      return t('views.pricing.planMonthly', 'Monthly');
    case 'pack-quarterly':
      return t('views.pricing.planQuarterly', 'Quarterly');
    case 'pack-halfyearly':
      return t('views.pricing.planHalfYearly', 'Half-Yearly');
    default:
      return packId;
  }
}

export function localizedPackPeriodShort(packId: string, t: TFunction): string {
  switch (packId) {
    case 'pack-monthly':
      return t('views.pricing.perMonth', 'per month');
    case 'pack-quarterly':
      return t('views.accountSettings.billing.period3Months', '3 months');
    case 'pack-halfyearly':
      return t('views.accountSettings.billing.period6Months', '6 months');
    default:
      return '';
  }
}

export function localizedPackPeriodDisplay(packId: string, t: TFunction): string {
  switch (packId) {
    case 'pack-monthly':
      return t('views.pricing.perMonthSlash', { defaultValue: '/ month' }).replace(/^\//, '').trim();
    case 'pack-quarterly':
      return t('views.pricing.per3Months', { defaultValue: '/ 3 months' }).replace(/^\//, '').trim();
    case 'pack-halfyearly':
      return t('views.pricing.per6Months', { defaultValue: '/ 6 months' }).replace(/^\//, '').trim();
    default:
      return '';
  }
}

export function formatLocalizedPlanTimeline(
  periodEnd: string | null,
  daysLeft: number | null,
  isExpired: boolean,
  t: TFunction,
  locale: string,
): string | null {
  if (!periodEnd) return null;
  const end = new Date(periodEnd);
  if (Number.isNaN(end.getTime())) return null;
  const formatted = end.toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  if (isExpired) {
    return t('creditsPurchaseModal.planTimelineExpired', {
      defaultValue: 'Expired {{date}}',
      date: formatted,
    });
  }
  if (daysLeft != null && daysLeft <= 0) {
    return t('creditsPurchaseModal.planTimelineEnded', {
      defaultValue: 'Ended {{date}}',
      date: formatted,
    });
  }
  if (daysLeft != null && daysLeft <= 7) {
    return t('creditsPurchaseModal.planTimelineRenewsSoon', {
      defaultValue: 'Renews in {{count}} days · {{date}}',
      count: daysLeft,
      date: formatted,
    });
  }
  return t('creditsPurchaseModal.planTimelineRenews', {
    defaultValue: 'Renews {{date}}',
    date: formatted,
  });
}
